import { useCallback, useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { FormDialog } from "../form";
import { api } from "../../api/client";

/**
 * Drag-to-reorder for a vendor's menu categories — the order customers see in
 * the category strip on the marketplace vendor screen.
 *
 * Categories are free-text `subCategory` labels on the listings rather than
 * rows of their own, so the list is whatever the vendor's menu currently uses
 * and the saved value is a list of labels. Categories the merchant hasn't
 * placed sort alphabetically at the end, so a newly added category shows up
 * here without invalidating an existing order.
 *
 * Uses the native HTML5 drag API to match the row reordering already in
 * ListingsPage (catalog-app has no drag-and-drop dependency).
 */
export default function CategoryOrderDialog({
  open,
  vendorId,
  vendorName,
  onClose,
  onSaved,
}) {
  const [categories, setCategories] = useState([]);
  const [initialOrder, setInitialOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [draggingLabel, setDraggingLabel] = useState(null);
  const [dragOverLabel, setDragOverLabel] = useState(null);

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCatalogVendorCategoryOrder(vendorId);
      const next = res?.data?.categories || [];
      setCategories(next);
      setInitialOrder(next);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const isDirty =
    categories.length !== initialOrder.length ||
    categories.some((label, index) => label !== initialOrder[index]);

  const moveCategory = (sourceLabel, targetLabel) => {
    if (!sourceLabel || sourceLabel === targetLabel) return;
    setCategories((prev) => {
      const next = [...prev];
      const fromIndex = next.indexOf(sourceLabel);
      const toIndex = next.indexOf(targetLabel);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleAutoSort = () =>
    setCategories((prev) =>
      [...prev].sort((a, b) =>
        a.trim().toLowerCase().localeCompare(b.trim().toLowerCase()),
      ),
    );

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateCatalogVendorCategoryOrder(vendorId, categories);
      if (onSaved) onSaved(categories);
      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to save category order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Arrange categories"
      subtitle={
        vendorName
          ? `Order customers see on ${vendorName}`
          : "Order customers see on the vendor screen"
      }
      error={error}
      submitting={saving}
      disableSubmit={!isDirty || loading || !categories.length}
      onSubmit={handleSubmit}
      submitLabel="Save order"
      extraActions={
        categories.length > 1 ? (
          <Button size="small" onClick={handleAutoSort} disabled={saving}>
            Sort A–Z
          </Button>
        ) : null
      }
    >
      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading categories…
        </Typography>
      ) : !categories.length ? (
        <Typography variant="body2" color="text.secondary">
          This vendor has no menu categories yet. Add listings with a category
          to arrange them here.
        </Typography>
      ) : (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Drag rows to reorder. Listings with no category always show last
            under “More”.
          </Typography>
          <Stack spacing={0.75}>
            {categories.map((label, index) => (
              <Box
                key={label}
                draggable={!saving}
                onDragStart={() => setDraggingLabel(label)}
                onDragEnter={() => setDragOverLabel(label)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  moveCategory(draggingLabel, label);
                  setDraggingLabel(null);
                  setDragOverLabel(null);
                }}
                onDragEnd={() => {
                  setDraggingLabel(null);
                  setDragOverLabel(null);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  border: 1,
                  borderRadius: 1,
                  borderColor:
                    dragOverLabel === label && draggingLabel !== label
                      ? "primary.main"
                      : "divider",
                  opacity: draggingLabel === label ? 0.5 : 1,
                  cursor: saving ? "default" : "grab",
                  userSelect: "none",
                }}
              >
                <DragIndicatorIcon fontSize="small" color="disabled" />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 20 }}
                >
                  {index + 1}
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      )}
    </FormDialog>
  );
}
